import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  LuCheck, LuMailSearch, LuPencil,
  LuChevronDown, LuCircleUser, LuFileText,
  LuPhone, LuSearch, LuUsers, LuCamera, LuCalendar1, LuInstagram, LuMessageSquareText, LuFingerprint,
} from 'react-icons/lu';
import { IoArrowBackSharp } from 'react-icons/io5';
import { FaRegImage, FaFolderOpen } from 'react-icons/fa';
import { ProgressPill, renderStepContent, VerificationCodeFields } from './RegisterSteps';
import { getStepIcon } from './StepIcons';
import { ReactComponent as TribeLogo } from '../../assets/TribeLogo.svg';
import MinimalFooter from '../../components/MinimalFooter';
import UnsavedChangesBottomSheet from '../../components/UnsavedChangesBottomSheet';
import { USER_TERMS_OF_SERVICE, USER_PRIVACY_POLICY } from '../../components/legal/userLegalText';
import {
  headingCircleContainer, stepHeadingStyle, stepSubTitleStyle,
  headerContainerStyle, headerLogoRowStyle, headerNavRowStyle,
  previousBackButtonStyle, logoSvgStyle, pageTitlePlainStyle, progressContainerStyle,
  getPrimaryButtonStyle, getSecondaryButtonStyle, labelUpperLogin, inputWideLogin,
  containerStyle, stepContainerNarrow, formContainerWide,
  highlightStyle, resendTextStyle, resendLinkStyle,
  alreadyHaveAccountStyle, loginLinkStyle,
  actionSheetStyles,
} from './RegisterStyles';

function birthDateISO(value) {
  const raw = String(value || '').trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  const us = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(raw);
  const [year, month, day] = iso ? iso.slice(1).map(Number) : us ? [Number(us[3]), Number(us[1]), Number(us[2])] : [];
  const date = new Date(Date.UTC(year, month - 1, day));
  if (!year || year < 1900 || date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return '';
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function adultBirthDateLimit(now = new Date()) {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now).map(part => [part.type, Number(part.value)]));
  const year = parts.year - 18;
  const day = Math.min(parts.day, new Date(Date.UTC(year, parts.month, 0)).getUTCDate());
  return `${year}-${String(parts.month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function isAdultBirthDate(value, now = new Date()) {
  const iso = birthDateISO(value);
  return Boolean(iso && iso <= adultBirthDateLimit(now));
}

function birthDateDisplay(value) {
  const iso = birthDateISO(value);
  return iso ? `${iso.slice(5, 7)}/${iso.slice(8, 10)}/${iso.slice(0, 4)}` : '';
}

const API = process.env.REACT_APP_API_URL || '';
const GENDER_LABELS = { male: 'Male', female: 'Female', 'non-binary': 'Non-binary', 'trans-man': 'Trans-Man', 'trans-woman': 'Trans-Woman' };
const PERSONALITY_TAGS = [
  {
    "emoji": "👂",
    "label": "Good listener"
  },
  {
    "emoji": "🗣️",
    "label": "Talkative"
  },
  {
    "emoji": "👋",
    "label": "Sociable"
  },
  {
    "emoji": "🌱",
    "label": "Quiet at first"
  },
  {
    "emoji": "☕",
    "label": "One-on-one"
  },
  {
    "emoji": "🤪",
    "label": "Goofy"
  },
  {
    "emoji": "🤫",
    "label": "Quiet company"
  },
  {
    "emoji": "🙋",
    "label": "Takes initiative"
  },
  {
    "emoji": "📣",
    "label": "Encouraging"
  },
  {
    "emoji": "📖",
    "label": "Storyteller"
  },
  {
    "emoji": "📅",
    "label": "Planner"
  },
  {
    "emoji": "🥐",
    "label": "Slow mornings"
  },
  {
    "emoji": "🪩",
    "label": "Busy weekends"
  },
  {
    "emoji": "🫶",
    "label": "Small groups"
  },
  {
    "emoji": "🏜️",
    "label": "Dry humor"
  },
  {
    "emoji": "🏓",
    "label": "Playful"
  },
  {
    "emoji": "🏡",
    "label": "Likes hosting"
  },
  {
    "emoji": "🎉",
    "label": "Big groups"
  },
  {
    "emoji": "🌿",
    "label": "Patient"
  },
  {
    "emoji": "🚀",
    "label": "Last-minute plans"
  },
  {
    "emoji": "🗺️",
    "label": "Likes structure"
  },
  {
    "emoji": "⚖️",
    "label": "Likes debate"
  },
  {
    "emoji": "🌊",
    "label": "Deep talks"
  },
  {
    "emoji": "🧭",
    "label": "Curious"
  },
  {
    "emoji": "💌",
    "label": "Thoughtful"
  },
  {
    "emoji": "🧩",
    "label": "Connector"
  },
  {
    "emoji": "🌅",
    "label": "Early bird"
  },
  {
    "emoji": "🛋️",
    "label": "Homebody"
  },
  {
    "emoji": "📝",
    "label": "Organized"
  },
  {
    "emoji": "🦉",
    "label": "Night owl"
  },
  {
    "emoji": "🆕",
    "label": "Adventurous"
  },
  {
    "emoji": "🏆",
    "label": "Competitive"
  },
  {
    "emoji": "🎲",
    "label": "Spontaneous"
  },
  {
    "emoji": "⚡",
    "label": "Energetic"
  },
  {
    "emoji": "🕯️",
    "label": "Low-key"
  },
  {
    "emoji": "🔋",
    "label": "Needs downtime"
  },
  {
    "emoji": "☀️",
    "label": "Active"
  },
  {
    "emoji": "🚶",
    "label": "Independent"
  },
  {
    "emoji": "🍃",
    "label": "Easygoing"
  },
  {
    "emoji": "🔁",
    "label": "Routine"
  }
];
const INITIAL = {
  firstName: '', lastName: '', email: '', phoneNumber: '', instagramHandle: '', gender: '', dateOfBirth: '',
  joinReason: '', personalityTags: [], otherReferrer: '', termsAccepted: false, applicationSmsOptIn: null,
};
const STEPS = [
  { id: 'basics', title: 'Basic Info', description: 'Add your name and email to continue.', icon: LuCircleUser },
  { id: 'verify', title: 'Verification Code', icon: LuMailSearch },
  { id: 'contact', title: 'Phone Number', description: 'Please provide your 10-digit phone number', icon: LuPhone },
  { id: 'verifyPhone', title: 'Verification Code', description: 'Enter the 3-digit code we texted you.', icon: LuPhone },
  { id: 'smsConsent', title: 'Text Notifications', description: 'We can text you updates about your membership application, including your invitation code if approved.', icon: LuMessageSquareText },
  { id: 'instagram', title: 'Instagram', description: 'What’s your Instagram handle?', icon: LuInstagram },
  { id: 'birthday', title: 'Gender & Birthday', description: 'We require this info to ensure you’re 18+.', icon: LuCalendar1 },
  { id: 'photo', title: 'Profile Picture', description: 'Please upload a JPG/PNG under 15MB.', icon: LuCamera },
  { id: 'joinReason', title: 'What made you want to join Tribe?', description: 'A sentence or two is plenty.', icon: LuCircleUser },
  { id: 'personalityTags', title: 'Which of these describe you?', description: 'Pick 5 that describe you best.', icon: LuFingerprint },
  { id: 'referral', title: 'Who referred you?', description: 'Choose who introduced you to Tribe so they get credit.', icon: LuUsers },
  { id: 'review', title: 'Review & apply', description: 'Check your details and submit your application.', icon: LuFileText },
];
// Member-application endpoints. These are separate from account creation.
// Callbacks can be injected for UI previews or connected to an existing API.
async function defaultLoadPartners({ signal }) {
  const response = await axios.get(`${API}/membership-applications/referral-partners`, { signal });
  if (!Array.isArray(response.data?.partners)) throw new Error('Invalid partner response');
  return response.data.partners;
}
async function defaultSubmitApplication(payload, { signal, idempotencyKey }) {
  const response = await axios.post(`${API}/membership-applications`, payload, {
    signal, headers: { 'Idempotency-Key': idempotencyKey },
  });
  return response.data;
}

async function defaultSendEmailCode({ email, firstName, lastName, signal }) {
  const response = await axios.post(`${API}/membership-applications/email/send-code`, { email, firstName, lastName }, { signal });
  return response.data;
}
async function defaultVerifyEmailCode({ email, challengeId, code, signal }) {
  const response = await axios.post(`${API}/membership-applications/email/verify`, { email, challengeId, code }, { signal });
  return response.data;
}

async function defaultSendPhoneCode(input) {
  const { signal, ...body } = input;
  return (await axios.post(`${API}/membership-applications/phone/send-code`, { ...body, purpose: 'application' }, { signal })).data;
}
async function defaultVerifyPhoneCode(input) {
  const { signal, ...body } = input;
  return (await axios.post(`${API}/membership-applications/phone/verify`, { ...body, purpose: 'application' }, { signal })).data;
}

function initials(name) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase();
}
function normalizePartners(rows) {
  const seen = new Set();
  return rows.flatMap(row => {
    const id = String(row.id || row._id || '');
    const name = String(row.displayName || [row.firstName, row.lastName].filter(Boolean).join(' ')).trim();
    if (!id || !name || seen.has(id)) return [];
    seen.add(id);
    return [{ id, name, handle: String(row.instagramHandle || row.handle || ''), photo: String(row.profilePictureUrl || row.photoUrl || '') }];
  });
}

function PartnerPhoto({ partner }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [partner.photo]);
  return <span className="ma-avatar" aria-hidden="true">
    {partner.photo && !failed ? <img src={partner.photo} alt="" onError={() => setFailed(true)} /> : initials(partner.name)}
  </span>;
}

function PartnerPicker({ partners, selected, onSelect, loading, error, onRetry, disabled }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const root = useRef(null), trigger = useRef(null), search = useRef(null);
  const id = useId();
  const choices = useMemo(() => {
    const needle = query.trim().toLowerCase().replace(/^@/, '');
    return [
      ...partners.filter(partner => `${partner.name} ${partner.handle}`.toLowerCase().includes(needle))
        .map(partner => ({ kind: 'partner', partnerId: partner.id, name: partner.name, partner })),
      { kind: 'other', name: 'Someone else' }, { kind: 'none', name: 'No one referred me' },
    ];
  }, [partners, query]);
  useEffect(() => { setActive(0); }, [query, partners]);
  useEffect(() => {
    if (!open) return undefined;
    const outside = event => { if (!root.current?.contains(event.target)) setOpen(false); };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open]);
  useEffect(() => {
    if (open) document.getElementById(`${id}-option-${active}`)?.scrollIntoView({ block: 'nearest' });
  }, [open, active, id]);
  function choose(choice) {
    onSelect(choice.kind === 'partner'
      ? { kind: 'partner', partnerId: choice.partnerId, name: choice.name, handle: choice.partner.handle }
      : { kind: choice.kind, name: choice.name });
    setOpen(false); setQuery(''); trigger.current?.focus({ preventScroll: true });
  }
  const chosenPartner = selected?.kind === 'partner' ? partners.find(partner => partner.id === selected.partnerId) : null;
  return <div className="ma-picker" ref={root}>
    <button ref={trigger} type="button" className="ma-picker-trigger" disabled={disabled}
      aria-label="Select who referred you" aria-haspopup="listbox" aria-expanded={open} aria-controls={open ? `${id}-list` : undefined}
      onClick={() => setOpen(value => !value)} onKeyDown={event => {
        if (event.key === 'ArrowDown') { event.preventDefault(); setOpen(true); }
      }}>
      <span className="ma-picker-selection">
        {chosenPartner ? <PartnerPhoto partner={chosenPartner} /> : null}
        <span className="ma-picker-person"><span>{selected?.name || 'Select a person'}</span>{chosenPartner?.handle ? <small>{chosenPartner.handle}</small> : null}</span>
      </span>
      <LuChevronDown size={17} aria-hidden="true" style={{ transform: open ? 'rotate(180deg)' : undefined }} />
    </button>
    {open ? <div className="ma-picker-panel">
      <div className="ma-search">
        <LuSearch size={16} aria-hidden="true" />
        <input ref={search} type="search" value={query} placeholder="Search name or handle"
          role="combobox" aria-label="Search Tribe partners" aria-autocomplete="list" aria-expanded={true}
          aria-controls={`${id}-list`} aria-activedescendant={`${id}-option-${Math.min(active, choices.length - 1)}`}
          onChange={event => setQuery(event.target.value)} onKeyDown={event => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault(); const delta = event.key === 'ArrowDown' ? 1 : -1;
              setActive(index => (index + delta + choices.length) % choices.length);
            } else if (event.key === 'Enter') {
              event.preventDefault(); choose(choices[Math.min(active, choices.length - 1)]);
            } else if (event.key === 'Escape') {
              event.preventDefault(); setOpen(false); trigger.current?.focus();
            }
          }} />
      </div>
      {loading ? <p className="ma-picker-note" role="status">Loading partners…</p> : null}
      {error ? <div className="ma-picker-note" role="status">We couldn’t load the partner list. <button type="button" className="ma-text-action" onClick={onRetry}>Try again</button></div> : null}
      {!loading && !error && choices.length === 2 ? <p className="ma-picker-note">{query.trim() ? 'No matching partner. You can select “Someone else” below.' : 'No partners are listed yet. If someone referred you, choose “Someone else” and enter their name or handle.'}</p> : null}
      <div id={`${id}-list`} role="listbox" aria-label="Who referred you" className="ma-options">
        {choices.map((choice, index) => <button key={choice.partnerId || choice.kind}
          id={`${id}-option-${index}`} type="button" role="option" tabIndex={-1}
          aria-selected={choice.kind === 'partner' ? selected?.partnerId === choice.partnerId : selected?.kind === choice.kind}
          className={`ma-option ${index === active ? 'ma-option-active' : ''} ${choice.kind !== 'partner' ? 'ma-option-other' : ''}`}
          onMouseMove={() => setActive(index)} onClick={() => choose(choice)}>
          {choice.partner ? <PartnerPhoto partner={choice.partner} /> : <span className="ma-avatar" aria-hidden="true"><LuUsers size={17} /></span>}
          <span className="ma-option-name"><span>{choice.name}</span>{choice.partner?.handle ? <small>{choice.partner.handle}</small> : null}</span>
          {(choice.kind === 'partner' ? selected?.partnerId === choice.partnerId : selected?.kind === choice.kind) ? <LuCheck size={17} aria-hidden="true" /> : null}
        </button>)}
      </div>
    </div> : null}
  </div>;
}

/**
 * UI for /apply. Requires the member-application API described in the README.
 * A successful callback MUST resolve { applicationId, status: 'submitted' }.
 * This component never creates an account, signs in, or awards referral credit.
 */
export default function MemberApplication({
  loadReferralPartners = defaultLoadPartners,
  onSubmitApplication = defaultSubmitApplication,
  sendEmailCode = defaultSendEmailCode,
  verifyEmailCode = defaultVerifyEmailCode,
  sendPhoneCode = defaultSendPhoneCode,
  verifyPhoneCode = defaultVerifyPhoneCode,
}) {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0), [form, setForm] = useState(INITIAL);
  // Fast Refresh may retain form state from before the tags field existed.
  const personalityTags = Array.isArray(form.personalityTags) ? form.personalityTags : [];
  const [typedBirthday, setTypedBirthday] = useState('');
  const [showGenderMenu, setShowGenderMenu] = useState(false);
  const [segFocus, setSegFocus] = useState({ seg2: false, seg3: false, seg4: false });
  const panel = useRef(null);
  const [code, setCode] = useState(''), [challenge, setChallenge] = useState(null);
  const [verified, setVerified] = useState(null), [resendAt, setResendAt] = useState(0), [now, setNow] = useState(Date.now());
  const [phoneCode, setPhoneCode] = useState(''), [phoneChallenge, setPhoneChallenge] = useState(null);
  const [verifiedPhone, setVerifiedPhone] = useState(null), [phoneResendAt, setPhoneResendAt] = useState(0);
  const [referrer, setReferrer] = useState(null);
  const [photo, setPhoto] = useState(null), [photoUrl, setPhotoUrl] = useState('');
  const photoInput = useRef(null);
  const [showProfileActionSheet, setShowProfileActionSheet] = useState(false);
  useEffect(() => {
    if (!photo) { setPhotoUrl(''); return undefined; }
    const url = URL.createObjectURL(photo); setPhotoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);
  const [partners, setPartners] = useState([]), [partnersLoading, setPartnersLoading] = useState(false);
  const [partnersError, setPartnersError] = useState(false), [partnersLoaded, setPartnersLoaded] = useState(false);
  const [retryPartners, setRetryPartners] = useState(0);
  const [error, setError] = useState(''), [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false), [discardOpen, setDiscardOpen] = useState(false);
  const [keyboardFocus, setKeyboardFocus] = useState(false);
  const heading = useRef(null), live = useRef(false), emailRef = useRef('');
  const busy = useRef(false), request = useRef(null), requestKey = useRef(null);
  const id = useId(), step = STEPS[index];
  const email = form.email.trim().toLowerCase();
  const emailVerified = Boolean(verified && verified.email === email && verified.expiresAt > Date.now());
  const phoneVerified = Boolean(verifiedPhone && verifiedPhone.email === email && verifiedPhone.phoneNumber === form.phoneNumber && verifiedPhone.expiresAt > Date.now());
  const phoneWaitSeconds = Math.max(0, Math.ceil((phoneResendAt - now) / 1000));
  const dirty = Boolean(form.firstName || form.lastName || form.email || form.phoneNumber || referrer);
  const waitSeconds = Math.max(0, Math.ceil((resendAt - now) / 1000));
  useEffect(() => { live.current = true; return () => { live.current = false; request.current?.abort(); }; }, []);
  useEffect(() => {
    const keyboard = event => {
      if (['Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Escape'].includes(event.key)) setKeyboardFocus(true);
    };
    const pointer = () => setKeyboardFocus(false);
    document.addEventListener('keydown', keyboard, true);
    document.addEventListener('pointerdown', pointer, true);
    return () => {
      document.removeEventListener('keydown', keyboard, true);
      document.removeEventListener('pointerdown', pointer, true);
    };
  }, []);
  useEffect(() => {
    const title = panel.current?.querySelector('h1, h2') || heading.current;
    title?.setAttribute('tabindex', '-1'); title?.focus({ preventScroll: true });
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [index, submitted]);
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return undefined;
    const visibleHeight = () => viewport.height * (viewport.scale || 1);
    let restingHeight = visibleHeight(), layoutWidth = window.innerWidth;
    let keyboardOpen = false, keyboardHeight = restingHeight, scrollingBack = false, scrollFrame;
    const editable = element => Boolean(element && panel.current?.contains(element)
      && !element.readOnly && !element.disabled
      && (element.tagName === 'TEXTAREA' || (element.tagName === 'INPUT'
        && ['text', 'email', 'tel', 'number', 'password', 'search', 'url'].includes(element.type))));
    const covered = () => restingHeight - visibleHeight() > Math.max(120, restingHeight * 0.18);
    const cancelReset = () => {
      window.cancelAnimationFrame(scrollFrame); scrollFrame = undefined;
      if (scrollingBack) window.scrollTo({ top: window.scrollY, left: window.scrollX, behavior: 'instant' });
      scrollingBack = false;
    };
    const returnToTop = () => {
      keyboardOpen = false;
      if (scrollFrame !== undefined) return;
      scrollFrame = window.requestAnimationFrame(() => {
        scrollFrame = undefined;
        if (document.body.style.overflow === 'hidden') return;
        if (editable(document.activeElement)) document.activeElement.blur();
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        scrollingBack = !reduceMotion;
        window.scrollTo({ top: 0, left: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
      });
    };
    const focusField = event => {
      if (!editable(event.target)) return;
      // A focus handoff between fields cancels the return before the next paint.
      cancelReset(); restingHeight = Math.max(restingHeight, visibleHeight());
      keyboardOpen = covered(); keyboardHeight = visibleHeight();
    };
    const blurField = event => {
      // Start with dismissal, without waiting for the keyboard's viewport animation.
      if (editable(event.target) && keyboardOpen) returnToTop();
    };
    const resize = () => {
      const height = visibleHeight();
      if (window.innerWidth !== layoutWidth) {
        layoutWidth = window.innerWidth; restingHeight = height; keyboardHeight = height;
        keyboardOpen = false; cancelReset(); return;
      }
      if (scrollFrame !== undefined) return;
      if (keyboardOpen) {
        keyboardHeight = Math.min(keyboardHeight, height);
        // Some keyboards hide without blurring. Start during recovery, not at full height.
        // Ignore small changes such as the keyboard's suggestion row.
        const closingGrowth = Math.max(96, (restingHeight - keyboardHeight) * 0.35);
        if (height - keyboardHeight >= closingGrowth || restingHeight - height <= 80) returnToTop();
      } else if (editable(document.activeElement) && covered()) {
        keyboardOpen = true; keyboardHeight = height; cancelReset();
      } else if (!editable(document.activeElement)) {
        restingHeight = Math.max(restingHeight, height);
      }
    };
    document.addEventListener('focusin', focusField);
    document.addEventListener('focusout', blurField);
    viewport.addEventListener('resize', resize);
    return () => {
      cancelReset(); document.removeEventListener('focusin', focusField);
      document.removeEventListener('focusout', blurField); viewport.removeEventListener('resize', resize);
    };
  }, []);
  useEffect(() => {
    if (!['verify', 'verifyPhone'].includes(step.id) || Math.max(resendAt, phoneResendAt) <= Date.now()) return undefined;
    setNow(Date.now()); const timer = window.setInterval(() => { setNow(Date.now()); if (Date.now() >= Math.max(resendAt, phoneResendAt)) window.clearInterval(timer); }, 1000);
    return () => window.clearInterval(timer);
  }, [step.id, resendAt, phoneResendAt]);
  useEffect(() => {
    if (!verified || submitted) return undefined;
    const timer = window.setTimeout(() => {
      setVerified(null); setCode(''); setIndex(1);
      setError('Your email verification expired. Request a new code to continue.');
    }, Math.min(2147483647, Math.max(0, verified.expiresAt - Date.now())));
    return () => window.clearTimeout(timer);
  }, [verified, submitted]);
  useEffect(() => {
    if (!verifiedPhone || submitted) return undefined;
    const timer = window.setTimeout(() => {
      setVerifiedPhone(null); setPhoneChallenge(null); setPhoneCode(''); setIndex(2);
      setError('Your phone verification expired. Verify your number again to continue.');
    }, Math.min(2147483647, Math.max(0, verifiedPhone.expiresAt - Date.now())));
    return () => window.clearTimeout(timer);
  }, [verifiedPhone, submitted]);
  useEffect(() => {
    if (!dirty || submitted) return undefined;
    const warn = event => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, submitted]);
  useEffect(() => {
    if (step.id !== 'referral' || partnersLoaded) return undefined;
    const controller = new AbortController(); let cancelled = false;
    setPartnersLoading(true); setPartnersError(false);
    Promise.resolve().then(() => loadReferralPartners({ signal: controller.signal })).then(rows => {
      if (cancelled) return;
      if (!Array.isArray(rows)) throw new Error('Invalid partner directory');
      setPartners(normalizePartners(rows)); setPartnersLoading(false); setPartnersLoaded(true);
    }).catch(() => { if (!cancelled) setPartnersError(true); })
      .finally(() => { if (!cancelled) setPartnersLoading(false); });
    return () => { cancelled = true; controller.abort(); };
  }, [step.id, partnersLoaded, retryPartners, loadReferralPartners]);

  function update(key, value) {
    if (busy.current) return;
    setError(''); requestKey.current = null;
    if (key === 'email') {
      emailRef.current = value.trim().toLowerCase();
      if (emailRef.current !== email) { setVerifiedPhone(null); setPhoneChallenge(null); setPhoneCode(''); setVerified(null); setChallenge(null); setCode(''); setResendAt(0); }
    }
    if (key === 'phoneNumber' && value !== form.phoneNumber) { setVerifiedPhone(null); setPhoneChallenge(null); setPhoneCode(''); }
    setForm(previous => ({ ...previous, [key]: value }));
  }
  function validStep(at) {
    switch (STEPS[at].id) {
      case 'basics': return Boolean(form.firstName.trim().length >= 2 && form.lastName.trim().length >= 2 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
      case 'verify': return emailVerified || Boolean(challenge && /^\d{3}$/.test(code));
      case 'contact': return /^[2-9]\d{2}[2-9]\d{6}$/.test(form.phoneNumber);
      case 'verifyPhone': return phoneVerified || Boolean(phoneChallenge && /^\d{3}$/.test(phoneCode));
      case 'smsConsent': return typeof form.applicationSmsOptIn === 'boolean';
      case 'instagram': return /^@?[a-zA-Z0-9._]{1,30}$/.test(form.instagramHandle.trim());
      case 'birthday': return Boolean(GENDER_LABELS[form.gender] && isAdultBirthDate(form.dateOfBirth));
      case 'photo': return Boolean(photo);
      case 'joinReason': return Boolean(form.joinReason.trim());
      case 'personalityTags': return personalityTags.length === 5;
      case 'referral': return Boolean(referrer && (referrer.kind !== 'other' || form.otherReferrer.trim().length >= 2));
      case 'review': return form.termsAccepted;
      default: return false;
    }
  }
  function back() {
    if (busy.current) return;
    setError('');
    if (step.id === 'smsConsent') setIndex(2);
    else if (index > 0) setIndex(value => value - 1);
    else if (dirty) setDiscardOpen(true);
    else navigate('/register');
  }
  async function sendCode(advance = false) {
    if (busy.current || !validStep(0) || (!advance && resendAt > Date.now())) return;
    busy.current = true; setSaving(true); setError('');
    const controller = new AbortController(); request.current = controller;
    const sentEmail = email;
    try {
      const result = await sendEmailCode({ email: sentEmail, firstName: form.firstName.trim(), lastName: form.lastName.trim(), signal: controller.signal });
      if (!live.current || controller.signal.aborted || emailRef.current !== sentEmail) return;
      if (!result || typeof result.challengeId !== 'string' || !result.challengeId) throw new Error('Unconfirmed code request');
      setChallenge(result.challengeId); setCode('');
      const cooldown = Number.isFinite(result.resendAfterSeconds) ? Math.min(300, Math.max(30, result.resendAfterSeconds)) : 60;
      setResendAt(Date.now() + cooldown * 1000); setNow(Date.now());
      if (advance) setIndex(1);
    } catch (failure) {
      if (live.current && !controller.signal.aborted) setError(failure?.response?.data?.message || 'We couldn’t send a verification code. Please try again.');
    } finally { busy.current = false; if (live.current) setSaving(false); }
  }
  async function verifyCode() {
    if (busy.current || !validStep(1)) return;
    if (emailVerified) { setIndex(2); return; }
    busy.current = true; setSaving(true); setError('');
    const controller = new AbortController(); request.current = controller;
    const sentEmail = email;
    try {
      const result = await verifyEmailCode({ email: sentEmail, challengeId: challenge, code, signal: controller.signal });
      if (!live.current || controller.signal.aborted || emailRef.current !== sentEmail) return;
      const expiresAt = Date.parse(result?.expiresAt);
      if (typeof result?.verificationToken !== 'string' || !result.verificationToken || result.email !== sentEmail || !Number.isFinite(expiresAt) || expiresAt <= Date.now()) throw new Error('Unconfirmed verification');
      setVerified({ token: result.verificationToken, email: sentEmail, expiresAt }); setCode(''); setIndex(2);
    } catch (failure) {
      if (live.current && !controller.signal.aborted) setError(failure?.response?.data?.message || 'We couldn’t verify that code. Check it and try again.');
    } finally { busy.current = false; if (live.current) setSaving(false); }
  }
  async function requestPhoneCode(advance = false) {
    if (busy.current || !validStep(2) || (!advance && phoneResendAt > Date.now())) return;
    if (!emailVerified) { setIndex(1); setError('Verify your email first.'); return; }
    busy.current = true; setSaving(true); setError('');
    const controller = new AbortController(); request.current = controller;
    try {
      const result = await sendPhoneCode({ email, phoneNumber: form.phoneNumber, emailVerificationToken: verified.token, signal: controller.signal });
      if (!live.current || controller.signal.aborted) return;
      if (typeof result?.challengeId !== 'string') throw new Error('Unconfirmed verification text');
      setPhoneChallenge(result.challengeId); setPhoneCode(''); setVerifiedPhone(null);
      setPhoneResendAt(Date.now() + 60000); setNow(Date.now());
      if (advance) setIndex(3);
    } catch (failure) {
      if (live.current && !controller.signal.aborted) setError(failure?.response?.data?.message || 'We couldn’t send your verification text. Please try again.');
    } finally { busy.current = false; if (live.current) setSaving(false); }
  }
  async function confirmPhoneCode() {
    if (busy.current || !validStep(3)) return;
    if (phoneVerified) { setIndex(4); return; }
    busy.current = true; setSaving(true); setError('');
    const controller = new AbortController(); request.current = controller;
    try {
      const result = await verifyPhoneCode({ email, phoneNumber: form.phoneNumber, challengeId: phoneChallenge, code: phoneCode, signal: controller.signal });
      if (!live.current || controller.signal.aborted) return;
      const expiresAt = Date.parse(result?.expiresAt);
      if (!result?.verificationToken || result.email !== email || result.phoneNumber !== form.phoneNumber || !Number.isFinite(expiresAt) || expiresAt <= Date.now()) throw new Error('Unconfirmed phone verification');
      setVerifiedPhone({ token: result.verificationToken, email, phoneNumber: form.phoneNumber, expiresAt });setPhoneCode('');setIndex(4);
    } catch (failure) {
      if (live.current && !controller.signal.aborted) setError(failure?.response?.data?.message || 'We couldn’t verify that text code. Check it and try again.');
    } finally { busy.current = false; if (live.current) setSaving(false); }
  }
  async function next(event) {
    event.preventDefault();
    if (busy.current || submitted) return;
    if (!validStep(index)) { setError('Please complete this step to continue.'); return; }
    setError('');
    if (step.id === 'basics') { if (emailVerified) setIndex(2); else await sendCode(true); return; }
    if (step.id === 'verify') { await verifyCode(); return; }
    if (step.id === 'contact') { if (phoneVerified) setIndex(4); else await requestPhoneCode(true); return; }
    if (step.id === 'verifyPhone') { await confirmPhoneCode(); return; }
    if (index < STEPS.length - 1) { setIndex(value => value + 1); return; }
    if (!emailVerified) { setIndex(1); setError('Verify your email before submitting your application.'); return; }
    if (!phoneVerified) { setIndex(2); setError('Verify your phone number before submitting your application.'); return; }
    const invalid = STEPS.findIndex((_, at) => !validStep(at));
    if (invalid !== -1) { setIndex(invalid); setError('Please check this information before applying.'); return; }
    busy.current = true; setSaving(true);
    const controller = new AbortController(); request.current = controller;
    if (!requestKey.current) requestKey.current = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const payload = {
      firstName: form.firstName.trim(), lastName: form.lastName.trim(), email,
      phoneNumber: form.phoneNumber, instagramHandle: `@${form.instagramHandle.trim().replace(/^@/, '')}`, gender: form.gender, dateOfBirth: form.dateOfBirth,
      emailVerificationToken: verified.token, phoneVerificationToken: verifiedPhone.token,
      answers: { joinReason: form.joinReason.trim() }, personalityTags: personalityTags,
      applicationSmsOptIn: form.applicationSmsOptIn,
      termsAccepted: true, referralSource: referrer.kind,
      ...(referrer.kind === 'partner' ? { referredByPartnerId: referrer.partnerId } : {}),
      ...(referrer.kind === 'other' ? { otherReferrer: form.otherReferrer.trim() } : {}),
    };
    try {
      const multipart = new FormData();
      for (const [key, value] of Object.entries(payload)) multipart.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
      multipart.append('profilePic', photo);
      const result = await onSubmitApplication(multipart, { signal: controller.signal, idempotencyKey: requestKey.current });
      if (!live.current || controller.signal.aborted) return;
      if (!result || typeof result.applicationId !== 'string' || !result.applicationId || result.status !== 'submitted') throw new Error('Unconfirmed application');
      setSubmitted(true);
    } catch (failure) {
      if (live.current && !controller.signal.aborted) setError(failure?.response?.data?.message || 'We couldn’t confirm your application was submitted. Please try again. Your details are still here.');
    } finally { busy.current = false; if (live.current) setSaving(false); }
  }

  const Icon = step.icon;
  const tagStep = step.id === 'personalityTags';
  const fixedActions = tagStep || step.id === 'review';
  function editReviewStep(stepId) {
    setError('');
    setIndex(STEPS.findIndex(item => item.id === stepId));
  }
  function handleProfilePicChange(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type) || !file.size || file.size > 15 * 1024 * 1024) {
      setError('Choose a JPG or PNG photo up to 15 MB.'); event.target.value = ''; return;
    }
    setPhoto(file); setError(''); requestKey.current = null;
  }
  // Use the registration renderer itself for screens with identical fields.
  const registrationStep = { basics: 2, contact: 5, birthday: 7, photo: 8 }[step.id];
  const continueStep = () => next({ preventDefault() {} });
  const registrationContent = registrationStep ? renderStepContent({
    step: registrationStep, inviteCode: '', password: '', confirmPassword: '',
    firstName: form.firstName, lastName: form.lastName, email: form.email,
    setFirstName: value => update('firstName', value), setLastName: value => update('lastName', value), setEmail: value => update('email', value),
    phoneNumber: form.phoneNumber, setPhoneNumber: value => update('phoneNumber', value),
    phoneCode, setPhoneCode: value => { setPhoneCode(value); setError(''); }, phoneBusy: saving, phoneWaitSeconds, phoneChallenge,
    requestPhoneVerification: requestPhoneCode, isSendingCode: saving,
    gender: form.gender, showGenderMenu, setShowGenderMenu,
    genderLabel: () => GENDER_LABELS[form.gender] || '--Select--',
    handleGenderSelect: value => { update('gender', value); setShowGenderMenu(false); },
    dateOfBirth: form.dateOfBirth, typedBirthday, validateAgeAtLeast18: isAdultBirthDate,
    handleBirthdayInput: raw => {
      let digits = raw.replace(/\D/g, '').slice(0, 8);
      if (digits.length > 2) digits = `${digits.slice(0, 2)}/${digits.slice(2)}`;
      if (digits.length > 5) digits = `${digits.slice(0, 5)}/${digits.slice(5)}`;
      setTypedBirthday(digits); update('dateOfBirth', birthDateISO(digits));
    },
    addToast: message => setError(message),
    profilePicFile: photo, profilePicPreview: photoUrl, fileInputRef: photoInput,
    setShowProfileActionSheet,
    handleProfilePicChange,
    segFocus, setSegFocus, anyBoxFocused: () => Object.values(segFocus).some(Boolean),
    moveFocus: (segment, direction) => {
      const nextSegment = ['seg2', 'seg3', 'seg4'].indexOf(segment) + direction;
      document.getElementById(['seg2', 'seg3', 'seg4'][nextSegment])?.focus();
    },
    shakeInputs: false, setShakeInputs: () => {}, handleNext: continueStep, handleBack: back,
  }) : null;
  const verificationStep = step.id === 'verify' || step.id === 'verifyPhone';
  const phoneVerification = step.id === 'verifyPhone';
  const verificationComplete = phoneVerification ? phoneVerified : emailVerified;
  const verificationWaitSeconds = phoneVerification ? phoneWaitSeconds : waitSeconds;
  const actionWidth = ['basics', 'smsConsent'].includes(step.id) ? 320 : 300;
  return <div className="ma-page" data-keyboard-focus={keyboardFocus ? 'true' : undefined}>
    <style>{STYLES + SUBMITTED_STYLES}</style>
    <header style={headerContainerStyle} className="ma-header">
      <div style={headerLogoRowStyle}><TribeLogo aria-label="Tribe" style={logoSvgStyle} /></div>
      {!submitted ? <div style={headerNavRowStyle}>
        {!submitted ? <button type="button" style={previousBackButtonStyle} className="ma-nav-back" disabled={saving} onClick={back} aria-label={index ? 'Previous step' : 'Back to registration'}><IoArrowBackSharp aria-hidden="true" /></button> : null}
        <span style={pageTitlePlainStyle}>Application</span>
        {!submitted ? <span style={progressContainerStyle} className="ma-progress" aria-label={`Step ${index + 1} of ${STEPS.length}`}><ProgressPill step={index + 1} total={STEPS.length} /></span> : null}
      </div> : null}
    </header>
    <main ref={panel} className="ma-main" style={submitted
      ? { ...containerStyle, width: '100%', maxWidth: 'none', margin: 0, display: 'flex', flexDirection: 'column', flex: 1 }
      : { ...containerStyle, flex: 1 }}>
      {submitted ? <MembershipApplicationSubmitted headingRef={heading} /> : registrationStep ? <>
        {registrationContent}
        {error ? <p className="ma-error" role="alert">{error}</p> : null}
        {index === 0 ? <div style={alreadyHaveAccountStyle}>Have an invite code?<Link to="/register" style={loginLinkStyle}>Sign up</Link></div> : null}
      </> : step.id === 'smsConsent' ? <>
        <ApplicationTextNotifications smsOptIn={form.applicationSmsOptIn} onChoice={value => update('applicationSmsOptIn', value)} onContinue={continueStep} onBack={back} />
        {error ? <p className="ma-error" role="alert">{error}</p> : null}
      </> : <form onSubmit={next} noValidate className={fixedActions ? 'ma-tag-step' : undefined} style={verificationStep ? stepContainerNarrow : tagStep ? { ...containerStyle, width: '100%' } : containerStyle}>
        <div style={headingCircleContainer}>{verificationStep ? getStepIcon(phoneVerification ? 5 : 3) : <Icon size={24} aria-hidden="true" />}</div>
        <h1 style={stepHeadingStyle} ref={heading} tabIndex={-1}>{step.title}</h1>
        <p style={{ ...stepSubTitleStyle, ...(tagStep ? { marginBottom: 6 } : {}) }}>{verificationStep ? <>Enter the 3-digit code we {step.id === 'verify' ? 'sent' : 'texted'} to<br /><span className="ph-mask" style={highlightStyle}>{step.id === 'verify' ? email : `+1 ${form.phoneNumber}`}</span></> : step.description}</p>
        {tagStep ? <p className="ma-tag-count" role="status" aria-live="polite">{personalityTags.length} of 5 selected</p> : null}
        <fieldset disabled={saving} className="ma-fields" style={{ ...formContainerWide, ...(tagStep ? { maxWidth: 'none' } : {}), ...(step.id === 'referral' ? { maxWidth: 300, marginBottom: '1.5rem' } : {}), ...(verificationStep ? { textAlign: 'center' } : {}) }} aria-label={step.title}>
          {verificationStep ? <>
            {verificationComplete ? <p className="ma-verified"><LuCheck size={17} aria-hidden="true" /> {phoneVerification ? 'Phone' : 'Email'} verified</p> : <VerificationCodeFields value={phoneVerification ? phoneCode : code} length={3} label={phoneVerification ? 'Phone verification code' : 'Email verification code'} error={Boolean(error)} disabled={saving} onChange={value => { if (phoneVerification) setPhoneCode(value); else setCode(value); setError(''); }} />}
            <p style={resendTextStyle}>Didn’t get the code? <button type="button" className="ma-resend-action" style={resendLinkStyle} disabled={saving || verificationWaitSeconds > 0} onClick={() => phoneVerification ? requestPhoneCode(false) : sendCode(false)}>{verificationWaitSeconds ? `Resend in ${verificationWaitSeconds}s` : 'Click to resend'}</button></p>
          </> : null}
          {step.id === 'instagram' ? <>
            <label htmlFor={`${id}-instagram`} style={labelUpperLogin}>Instagram</label><input id={`${id}-instagram`} style={inputWideLogin} className="ma-input" autoComplete="off" autoCapitalize="none" spellCheck={false} maxLength={31} placeholder="@username" value={form.instagramHandle} onChange={event => update('instagramHandle', event.target.value)} />
          </> : null}
          {step.id === 'joinReason' ? <>
            <label className="ma-sr-only" htmlFor={`${id}-answer`}>{step.title}</label>
            <textarea id={`${id}-answer`} className="ma-long-answer" maxLength={600} rows={4} placeholder="Tell us a little about why you’d like to join…" value={form[step.id]} onChange={event => update(step.id, event.target.value)} />
            <p className="ma-answer-count">{form[step.id].length}/600</p>
          </> : null}
          {tagStep ? <div className="ma-tag-list" role="group" aria-label="Personality and social style">
            {PERSONALITY_TAGS.map(tag => {
              const selected = personalityTags.includes(tag.label);
              return <button key={tag.label} type="button" className={`ma-tag${selected ? ' ma-tag-selected' : ''}`} aria-pressed={selected}
                disabled={saving || (!selected && personalityTags.length === 5)}
                onClick={() => update('personalityTags', selected ? personalityTags.filter(label => label !== tag.label) : [...personalityTags, tag.label])}>
                <span className="ma-tag-emoji" aria-hidden="true">{tag.emoji}</span><span>{tag.label}</span>
              </button>;
            })}
          </div> : null}
          {step.id === 'referral' ? <>
            <PartnerPicker partners={partners} selected={referrer} loading={partnersLoading} error={partnersError} disabled={saving} onRetry={() => setRetryPartners(value => value + 1)} onSelect={value => { setReferrer(value); setError(''); requestKey.current = null; }} />
            {referrer?.kind === 'other' ? <label style={labelUpperLogin} className="ma-other-referrer">Their name or social handle<input className="ma-input" style={inputWideLogin} value={form.otherReferrer} maxLength={100} onChange={event => update('otherReferrer', event.target.value)} placeholder="Name or @handle" /></label> : null}
          </> : null}
          {step.id === 'review' ? <>
            <div className="ma-review">
              <div className="ma-review-profile">
                <input ref={photoInput} type="file" accept="image/*" onChange={handleProfilePicChange} hidden />
                <button type="button" className="ma-review-photo" aria-label="Change profile photo" onClick={() => setShowProfileActionSheet(true)}>
                  {photoUrl ? <img src={photoUrl} alt="Your profile" /> : <LuCamera size={22} aria-hidden="true" />}
                  <span><LuPencil size={11} aria-hidden="true" /></span>
                </button>
                <div className="ma-review-identity">
                  <button type="button" className="ma-review-name" aria-label="Edit name" onClick={() => editReviewStep('basics')}>{form.firstName.trim()} {form.lastName.trim()}</button>
                  <button type="button" className="ma-review-handle" aria-label="Edit Instagram" onClick={() => editReviewStep('instagram')}>@{form.instagramHandle.trim().replace(/^@/, '')}<LuPencil size={11} aria-hidden="true" /></button>
                </div>
              </div>
              <ReviewSummaryRow title="Email" onEdit={() => editReviewStep('basics')}><span>{email}</span></ReviewSummaryRow>
              <ReviewSummaryRow title="Phone number" onEdit={() => editReviewStep('contact')}><span>{form.phoneNumber.replace(/^(\d{3})(\d{3})(\d{4})$/, '($1) $2-$3')}</span></ReviewSummaryRow>
              <ReviewSummaryRow title="Gender & birthday" onEdit={() => editReviewStep('birthday')}>
                <span>{GENDER_LABELS[form.gender]} · {birthDateDisplay(form.dateOfBirth)}</span>
              </ReviewSummaryRow>
              <details className="ma-review-answers">
                <summary><span><span className="ma-review-label">Your answers</span><span className="ma-review-value">Why you’d like to join · {personalityTags.length} tags</span></span><LuChevronDown size={17} aria-hidden="true" /></summary>
                <ReviewSummaryRow title="What made you want to join Tribe?" onEdit={() => editReviewStep('joinReason')}><span className="ma-review-full-answer">{form.joinReason}</span></ReviewSummaryRow>
                <ReviewSummaryRow title="Your personality" onEdit={() => editReviewStep('personalityTags')}><span className="ma-review-tags">{personalityTags.map(label => <span key={label}>{PERSONALITY_TAGS.find(tag => tag.label === label)?.emoji} {label}</span>)}</span></ReviewSummaryRow>
              </details>
              <ReviewSummaryRow title="Referred by" onEdit={() => editReviewStep('referral')}>
                <span>{referrer?.kind === 'other' ? form.otherReferrer : referrer?.kind === 'none' ? 'No one' : referrer?.name}</span>
              </ReviewSummaryRow>
            </div>
            <details className="ma-legal"><summary>Terms of Service &amp; Privacy Policy <LuChevronDown size={14} aria-hidden="true" /></summary><div tabIndex={0} className="ma-legal-text" aria-label="Terms and privacy policy">{USER_TERMS_OF_SERVICE}{'\n\n'}{USER_PRIVACY_POLICY}</div></details>
            <div className="ma-review-consent">
              <input id={`${id}-terms`} type="checkbox" checked={form.termsAccepted} onChange={event => update('termsAccepted', event.target.checked)} />
              <label htmlFor={`${id}-terms`}>I agree to Tribe’s <Link to="/terms" target="_blank">Terms of Service</Link> and <Link to="/privacy" target="_blank">Privacy Policy</Link>.</label>
            </div>
          </> : null}
        </fieldset>
        {error ? <p className="ma-error" role="alert">{error}</p> : null}
        <div className={fixedActions ? 'ma-tag-actions' : undefined}>
        <div className={fixedActions ? 'ma-tag-buttons' : undefined}>
        {fixedActions ? <button type="button" className="ma-back" style={{ ...getSecondaryButtonStyle(actionWidth), flex: 1, maxWidth: 'none', margin: 0, height: 42 }} disabled={saving} onClick={back}>Back</button> : null}
        <button type="submit" className="ma-next" style={{ ...getPrimaryButtonStyle(Boolean(validStep(index)) && !saving, actionWidth), ...(fixedActions ? { flex: 1, minWidth: 0, maxWidth: 'none', margin: 0, padding: '0 10px', whiteSpace: 'nowrap' } : {}) }} disabled={!validStep(index) || saving} aria-busy={saving}>
          {saving ? (['basics', 'contact'].includes(step.id) ? 'Sending…' : verificationStep ? 'Verifying…' : 'Submitting…') : index === STEPS.length - 1 ? 'Submit application' : 'Continue'}
        </button>
        </div>
        </div>
        {!fixedActions ? <button type="button" className="ma-back" style={getSecondaryButtonStyle(actionWidth)} disabled={saving} onClick={back}>Back</button> : null}
        {index === 0 ? <div style={alreadyHaveAccountStyle}>Have an invite code?<Link to="/register" style={loginLinkStyle}>Sign up</Link></div> : null}
      </form>}
    </main>
    {submitted ? <MinimalFooter /> : null}
    {showProfileActionSheet && ['photo', 'review'].includes(step.id) ? <ApplicationPhotoActionSheet
      onClose={() => setShowProfileActionSheet(false)}
      onChoose={() => { setShowProfileActionSheet(false); photoInput.current?.click(); }}
    /> : null}
    {discardOpen ? <UnsavedChangesBottomSheet onCancel={() => setDiscardOpen(false)} onDiscard={() => { setDiscardOpen(false); navigate('/register'); }} /> : null}
  </div>;
}


// Match the creator and host confirmation screens after a confirmed submission.
function MembershipApplicationSubmitted({ headingRef }) {
  return (
      <section
        className="ma-submitted-content"
        aria-labelledby="member-application-submitted-title"
      >
        <div className="ma-submitted-mark" aria-hidden="true">
          <LuCheck size={20} strokeWidth={2.5} />
        </div>

        <h1 ref={headingRef} tabIndex={-1} id="member-application-submitted-title">Application received.</h1>

        <p className="ma-submitted-copy">
          Thanks for applying to Tribe. We’ll review your application and email
          you an invitation if approved.
        </p>

        <Link className="ma-submitted-button" to="/events">
          Back to Tribe
        </Link>
      </section>
  );
}

const SUBMITTED_STYLES = `
  .ma-submitted-content h1:focus {
    outline: none;
  }

  .ma-submitted-content {
    flex: 1;
    display: grid;
    place-content: center;
    justify-items: center;
    width: min(100%, 620px);
    margin: 0 auto;
    padding: 48px 22px 120px;
    box-sizing: border-box;
    text-align: center;
  }

  .ma-submitted-mark {
    display: grid;
    place-items: center;
    width: 42px;
    height: 42px;
    margin-bottom: 18px;
    border-radius: 50%;
    background: #151515;
    color: #fff;
  }

  .ma-submitted-content h1 {
    margin: 0;
    color: #171717;
    font-size: clamp(32px, 3.4vw, 46px);
    font-weight: 720;
    line-height: 1.03;
    letter-spacing: -0.045em;
  }

  .ma-submitted-copy {
    max-width: 500px;
    margin: 16px auto 0;
    color: #62625d;
    font-size: clamp(13px, 1.05vw, 15px);
    line-height: 1.55;
  }

  .ma-submitted-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 160px;
    height: 46px;
    margin-top: 24px;
    padding: 0 22px;
    box-sizing: border-box;
    border: 1px solid #151515;
    border-radius: 999px;
    background: #151515;
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    text-decoration: none;
  }

`;


// Keep this panel in lockstep with registration’s Text Notifications screen.
function ApplicationTextNotifications({ smsOptIn, onChoice, onContinue, onBack }) {

      const smsChoiceMade =
        smsOptIn === true ||
        smsOptIn === false;

      const smsYesSelected =
        smsOptIn === true;

      const smsNoSelected =
        smsOptIn === false;

      return (
        <div
          style={
            containerStyle
          }
        >
          <div
            style={
              headingCircleContainer
            }
          >
            {getStepIcon(6)}
          </div>

          <h2
            style={
              stepHeadingStyle
            }
          >
            Text Notifications
          </h2>

          <p
            style={{
              ...stepSubTitleStyle,
              maxWidth: 340,
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
We can text you updates about your membership application, including your invitation code if approved.          </p>

          <div
            style={{
              width: '100%',
              maxWidth: 320,
              margin: '0 auto',
            }}
          >
            <button
              type="button"
              onClick={() => {
                onChoice(
                  true
                );
              }}
              style={{
                width: '100%',
                minHeight: 52,
                padding: '10px 12px',
                border:
                  smsYesSelected
                    ? '1.5px solid #0b0b0b'
                    : '1px solid #d1d5db',
                borderRadius: 12,
                background:
                  smsYesSelected
                    ? '#f7f7f7'
                    : '#fff',
                color: '#0b0b0b',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent:
                  'space-between',
                gap: 10,
                boxSizing:
                  'border-box',
                fontFamily:
                  "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
                WebkitTapHighlightColor:
                  'transparent',
              }}
            >
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  minWidth: 0,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 18,
                    height: 18,
                    minWidth: 18,
                    borderRadius: '50%',
                    border:
                      '1.5px solid #0b0b0b',
                    boxSizing:
                      'border-box',
                    background:
                      smsYesSelected
                        ? '#0b0b0b'
                        : '#fff',
                    boxShadow:
                      smsYesSelected
                        ? 'inset 0 0 0 4px #fff'
                        : 'none',
                  }}
                />

                <span
                  style={{
                    fontSize: 13,
                    fontWeight:
                      smsYesSelected
                        ? 800
                        : 750,
                    lineHeight: 1.2,
                    textAlign: 'left',
                  }}
                >
                  Yes, enable text updates
                </span>
              </span>

              <span
                style={{
                  flexShrink: 0,
                  padding: '4px 7px',
                  borderRadius: 999,
                  background:
                    '#0b0b0b',
                  color: '#fff',
                  fontSize: 9.5,
                  lineHeight: 1,
                  fontWeight: 800,
                  letterSpacing:
                    '0.01em',
                }}
              >
                Recommended
              </span>
            </button>

            {!smsYesSelected && (
              <button
                type="button"
                onClick={() => {
                  onChoice(
                    false
                  );
                }}
                style={{
                  width: '100%',
                  minHeight: 52,
                  padding: '10px 12px',
                  marginTop: 10,
                  border:
                    smsNoSelected
                      ? '1.5px solid #0b0b0b'
                      : '1px solid #e5e7eb',
                  borderRadius: 12,
                  background:
                    smsNoSelected
                      ? '#f7f7f7'
                      : '#fff',
                  color: '#0b0b0b',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  boxSizing:
                    'border-box',
                  fontFamily:
                    "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
                  WebkitTapHighlightColor:
                    'transparent',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 18,
                    height: 18,
                    minWidth: 18,
                    borderRadius: '50%',
                    border:
                      '1.5px solid #0b0b0b',
                    boxSizing:
                      'border-box',
                    background:
                      smsNoSelected
                        ? '#0b0b0b'
                        : '#fff',
                    boxShadow:
                      smsNoSelected
                        ? 'inset 0 0 0 4px #fff'
                        : 'none',
                  }}
                />

                <span
                  style={{
                    fontSize: 13,
                    fontWeight:
                      smsNoSelected
                        ? 800
                        : 650,
                    lineHeight: 1.2,
                    textAlign: 'left',
                  }}
                >
                  No, not now
                </span>
              </button>
            )}

            <p
              style={{
                maxWidth: 320,
                margin: '1rem auto 0',
                fontSize: '0.72rem',
                lineHeight: 1.45,
                color: '#6b7280',
                textAlign: 'left',
              }}
            >
              Text me updates about my Tribe membership application, including an invitation code if approved. Message frequency varies. Msg &amp; data rates may apply. Reply STOP to opt out. Consent is optional.{' '}
              <a
                href="/terms"
                target="_blank"
                rel="noreferrer"
                style={{
                  color: '#0b0b0b',
                  fontWeight: 700,
                  textDecoration:
                    'underline',
                }}
              >
                Terms
              </a>
              {' · '}
              <a
                href="/privacy"
                target="_blank"
                rel="noreferrer"
                style={{
                  color: '#0b0b0b',
                  fontWeight: 700,
                  textDecoration:
                    'underline',
                }}
              >
                Privacy
              </a>
            </p>

            <p
              style={{
                maxWidth: 320,
                margin: '0.5rem auto 0',
                fontSize: '0.72rem',
                lineHeight: 1.45,
                color: '#9ca3af',
                textAlign: 'left',
              }}
            >
              Text updates are optional and are not required to apply.
            </p>
          </div>

          {smsChoiceMade && (
            <button
              type="button"
              onClick={
                onContinue
              }
              style={{
                ...getPrimaryButtonStyle(
                  true,
                  320
                ),
                marginTop: '1rem',
              }}
            >
              Continue
            </button>
          )}

          <button
            type="button"
            onClick={
              onBack
            }
            style={{
              ...getSecondaryButtonStyle(
                320
              ),
              marginTop:
                smsChoiceMade
                  ? '0.75rem'
                  : '1rem',
            }}
          >
            Back
          </button>
        </div>
      );
    
}

function ReviewSummaryRow({ title, children, onEdit }) {
  return <div className="ma-review-row">
    <div className="ma-review-row-content"><span className="ma-review-label">{title}</span><div className="ma-review-value">{children}</div></div>
    <button type="button" className="ma-review-edit" onClick={onEdit} aria-label={`Edit ${title.toLowerCase()}`}>Edit</button>
  </div>;
}

function ApplicationPhotoActionSheet({ onClose, onChoose }) {
  const panel = useRef(null), firstOption = useRef(null);
  useEffect(() => {
    const previous = document.activeElement, overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden'; firstOption.current?.focus();
    function keys(event) {
      if (event.key === 'Escape') onClose();
      if (event.key !== 'Tab') return;
      const buttons = panel.current?.querySelectorAll('button');
      if (!buttons?.length) return;
      const first = buttons[0], last = buttons[buttons.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
    document.addEventListener('keydown', keys);
    return () => { document.body.style.overflow = overflow; document.removeEventListener('keydown', keys); previous?.focus?.(); };
  }, [onClose]);
  const optionStyle = { width: '100%', border: 0, background: 'transparent', font: 'inherit', textAlign: 'left' };
  return <div style={actionSheetStyles.backdrop} onClick={onClose}>
    <div ref={panel} role="dialog" aria-modal="true" aria-label="Upload profile photo" style={actionSheetStyles.sheetContainer} onClick={event => event.stopPropagation()}>
      <div style={actionSheetStyles.optionContainer}>
        <button ref={firstOption} type="button" style={{ ...optionStyle, ...actionSheetStyles.optionRow, ...actionSheetStyles.optionRowTop }} onClick={onChoose}>
          <span>Photo Library</span><FaRegImage style={actionSheetStyles.iconRight} aria-hidden="true" />
        </button>
        <button type="button" style={{ ...optionStyle, ...actionSheetStyles.optionRow, ...actionSheetStyles.optionRowBottom }} onClick={onChoose}>
          <span>Choose from Files</span><FaFolderOpen style={actionSheetStyles.iconRight} aria-hidden="true" />
        </button>
      </div>
      <button type="button" style={{ width: '100%', border: 0, font: 'inherit', ...actionSheetStyles.cancelRow }} onClick={onClose}>Cancel</button>
    </div>
    <style>{`@keyframes slideUp { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
  </div>;
}

const STYLES = `
  /* Page and navigation */

  .ma-page {
    min-height: 100vh;
    min-height: 100dvh;
    display: flex;
    flex-direction: column;
    background: #fff;
    color: #0b0b0b;
    font-family: Inter, system-ui, -apple-system, "Segoe UI", sans-serif;
  }

  .ma-page button {
    cursor: pointer;
  }

  .ma-page button:disabled {
    cursor: not-allowed;
  }

  .ma-page :focus {
    outline: none;
  }

  .ma-page[data-keyboard-focus="true"] :is(button, a, summary, input[type="checkbox"], [tabindex="0"]):focus-visible {
    outline: 2px solid #8a8a84;
    outline-offset: 3px;
  }

  .ma-page h1:focus,
  .ma-page h2:focus {
    outline: none;
  }

  .ma-nav-back {
    z-index: 1001;
  }

  .ma-main {
    flex: 1;
  }

  .ma-main h1 {
    scroll-margin-top: 120px;
  }

  /* Form fields */

  .ma-fields {
    border: 0;
    padding: 0;
    min-width: 0;
    text-align: left;
  }

  .ma-fields:disabled {
    opacity: 0.75;
  }

  .ma-input {
    display: block;
    min-width: 0;
  }

  .ma-input:focus {
    border-color: #999;
  }

  .ma-phone-segments {
    display: flex;
    justify-content: center;
    gap: 0.5rem;
    margin: 0 auto 1.5rem;
  }

  .ma-phone-segments input {
    flex-shrink: 1;
  }

  .registration-code-input::placeholder {
    color: #9ca3af;
    font-weight: 650;
    opacity: 1;
  }

  .ma-resend-action {
    border: 0;
    background: transparent;
    padding: 0;
    font: inherit;
    text-decoration: none;
  }

  .ma-resend-action:disabled {
    opacity: 0.55;
  }

  .ma-hint {
    color: #6b7280;
    font-size: 0.85rem;
    line-height: 1.45;
  }

  .ma-text-action {
    border: 0;
    background: none;
    color: #0b0b0b;
    padding: 6px 0;
    text-decoration: underline;
    text-underline-offset: 3px;
    font: inherit;
    font-size: 0.85rem;
  }

  /* Consent */

  .ma-consent {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 0.72rem;
    line-height: 1.45;
    color: #6b7280;
    margin: 1rem 0;
    text-align: left;
  }

  .ma-consent input {
    flex: none;
    width: 17px;
    height: 17px;
    margin: 2px 0 0;
    accent-color: #111;
  }

  .ma-consent a {
    color: #0b0b0b;
    text-underline-offset: 2px;
  }

  /* Referral picker */

  .ma-picker {
    position: relative;
  }

  .ma-picker-trigger {
    box-sizing: border-box;
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 48px;
    border: 1px solid transparent;
    border-radius: 14px;
    background: #f5f5f5;
    padding: 10px 14px;
    font: inherit;
    font-size: 0.9rem;
    color: #0b0b0b;
    text-align: left;
  }

  .ma-picker-selection {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  .ma-picker-selection > span:last-child {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ma-picker-person {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
  }

  .ma-picker-person > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ma-picker-person small {
    font-size: 12px;
    color: #6b7280;
  }

  .ma-picker-panel {
    margin-top: 8px;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    overflow: hidden;
    background: #fff;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.035);
  }

  .ma-search {
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 8px 12px;
    border-bottom: 1px solid #eee;
    color: #888;
  }

  .ma-search input {
    width: 100%;
    min-width: 0;
    font: inherit;
    font-size: 16px;
    border: 0;
    outline: 0;
    padding: 8px 0;
    background: #fff;
    color: #0b0b0b;
  }

  .ma-options {
    max-height: 288px;
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    padding: 4px;
  }

  .ma-option {
    display: flex;
    align-items: center;
    gap: 11px;
    width: 100%;
    padding: 10px;
    border: 0;
    border-radius: 8px;
    background: #fff;
    color: #0b0b0b;
    text-align: left;
    font: inherit;
    font-size: 0.9rem;
  }

  .ma-option-active,
  .ma-option:hover {
    background: #f3f4f6;
  }

  .ma-avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #f3f4f6;
    color: #6b7280;
    font-size: 11px;
    font-weight: 600;
    overflow: hidden;
  }

  .ma-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .ma-option-name {
    display: flex;
    flex-direction: column;
    gap: 3px;
    flex: 1;
    min-width: 0;
    overflow-wrap: anywhere;
  }

  .ma-option-name small {
    font-size: 12px;
    color: #6b7280;
  }

  .ma-option-other {
    border-top: 1px solid #f3f4f6;
    border-radius: 0;
  }

  .ma-picker-note {
    font-size: 0.85rem;
    color: #6b7280;
    line-height: 1.45;
    margin: 12px 14px;
  }

  .ma-other-referrer {
    display: block;
    margin-top: 22px;
  }

  .ma-referral-hint {
    margin: 12px 0 0;
  }

  /* Compact application review */

  .ma-review { margin: 0 0 20px; text-align: left; }
  .ma-review-profile { display: flex; align-items: center; gap: 14px; padding: 0 0 18px; border-bottom: 1px solid #e9e9e9; }
  .ma-review-photo { position: relative; flex: none; width: 60px; height: 60px; padding: 0; border: 0; border-radius: 50%; background: #f3f4f6; color: #777; }
  .ma-review-photo > img { display: block; width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }
  .ma-review-photo > span { position: absolute; right: -2px; bottom: -2px; display: grid; place-items: center; width: 22px; height: 22px; border: 2px solid #fff; border-radius: 50%; background: #f0f0f0; color: #555; }
  .ma-review-identity { display: flex; flex-direction: column; align-items: flex-start; gap: 5px; min-width: 0; }
  .ma-review-name, .ma-review-handle { border: 0; background: none; padding: 0; font: inherit; text-align: left; overflow-wrap: anywhere; }
  .ma-review-name { font-size: 17px; font-weight: 650; line-height: 1.3; letter-spacing: -.02em; color: #151515; }
  .ma-review-handle { display: flex; align-items: center; gap: 7px; font-size: 13px; line-height: 1.4; color: #737373; }
  .ma-review-handle svg { flex: none; }
  .ma-review-row { display: flex; align-items: center; justify-content: space-between; gap: 14px; width: 100%; padding: 12px 0; border: 0; border-bottom: 1px solid #e9e9e9; background: transparent; color: #777; text-align: left; font: inherit; }
  .ma-review-row:last-child { border-bottom: 0; padding-bottom: 0; }
  .ma-review-row-content { display: flex; flex-direction: column; gap: 5px; min-width: 0; flex: 1; }
  .ma-review-label { color: #737373; font-size: 12px; line-height: 1.4; }
  .ma-review-value { display: flex; flex-direction: column; gap: 3px; color: #202020; font-size: 14px; line-height: 1.45; overflow-wrap: anywhere; }
  .ma-review-edit { flex: none; border: 0; background: none; padding: 8px 0 8px 8px; font: inherit; font-size: 12px; color: #777; text-decoration: underline; text-underline-offset: 3px; }
  .ma-review-answers { border-bottom: 1px solid #e9e9e9; }
  .ma-review-answers summary { display: flex; align-items: center; justify-content: space-between; gap: 14px; padding: 14px 0; list-style: none; cursor: pointer; }
  .ma-review-answers summary::-webkit-details-marker { display: none; }
  .ma-review-answers summary > span { display: flex; flex-direction: column; gap: 5px; }
  .ma-review-answers summary > svg { flex: none; color: #939393; }
  .ma-review-answers[open] summary > svg { transform: rotate(180deg); }
  .ma-review-answers .ma-review-row { align-items: flex-start; }
  .ma-review-answers .ma-review-row:last-child { padding-bottom: 16px; }
  .ma-review-full-answer { white-space: pre-wrap; }
  .ma-review-consent { display: flex; align-items: flex-start; gap: 9px; margin: 16px 0 22px; text-align: left; color: #737373; font-size: 12px; line-height: 1.6; }
  .ma-review-consent input { flex: none; width: 17px; height: 17px; margin: 2px 0 0; accent-color: #111; }
  .ma-review-consent a { display: inline; padding: 0; border: 0; background: none; color: #333; text-decoration: underline; text-underline-offset: 2px; font: inherit; }

  /* Terms and privacy */

  .ma-legal {
    font-size: 0.85rem;
    border-bottom: 1px solid #e5e7eb;
    padding: 0 0 14px;
  }

  .ma-legal summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    cursor: pointer;
    color: #6b7280;
    list-style: none;
    min-height: 36px;
  }

  .ma-legal summary::-webkit-details-marker {
    display: none;
  }

  .ma-legal[open] summary svg {
    transform: rotate(180deg);
  }

  .ma-legal-text {
    white-space: pre-wrap;
    font-size: 0.8rem;
    line-height: 1.7;
    color: #6b7280;
    max-height: 260px;
    overflow-y: auto;
    margin-top: 12px;
    padding: 12px;
    border: 1px solid #eee;
    border-radius: 8px;
  }

  /* Buttons and messages */

  .ma-next {
    display: block;
    margin: 0 auto;
  }

  .ma-back {
    display: block;
    margin-inline: auto;
  }

  .ma-error {
    background: #fff6f4;
    border: 1px solid #efdbd7;
    color: #a03c32;
    padding: 11px 13px;
    border-radius: 8px;
    font-size: 0.85rem;
    line-height: 1.45;
    margin: 0 0 1rem;
    text-align: left;
  }

  .ma-primary-link {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    text-decoration: none;
    margin: 28px auto 0;
  }

  .ma-success {
    width: 100%;
    max-width: 320px;
    margin: 1.25rem auto;
    text-align: center;
  }

  .ma-sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    clip: rect(0, 0, 0, 0);
    overflow: hidden;
    white-space: nowrap;
  }

  .ma-verified {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #587254;
    font-size: 0.9rem;
    margin: 0 0 1.25rem;
  }

  .ma-verified-tag {
    font-size: 0.75rem;
    font-weight: 500;
    color: #67825c;
    margin-left: 4px;
  }

  /* Written answer */

  .ma-long-answer {
    display: block;
    width: 100%;
    box-sizing: border-box;
    resize: vertical;
    min-height: 135px;
    max-height: 360px;
    border: 1px solid transparent;
    border-radius: 14px;
    background: #f5f5f5;
    padding: 14px;
    font: inherit;
    font-size: 0.9rem;
    line-height: 1.6;
    color: #0b0b0b;
  }

  .ma-long-answer::placeholder {
    color: #9ca3af;
  }

  .ma-answer-count {
    font-size: 0.76rem;
    color: #6b7280;
    text-align: right;
    margin: 8px 0 1.25rem;
  }

  /* Personality tags */

  .ma-tag-step {
    padding-bottom: calc(120px + env(safe-area-inset-bottom, 0px));
  }

  .ma-tag-count {
    margin: 0 auto 8px;
    text-align: center;
    color: #6b7280;
    font-size: 12px;
    line-height: 1.4;
  }

  .ma-tag-list {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px 8px;
    margin: 12px 0 20px;
  }

  .ma-tag {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    max-width: 100%;
    min-height: 42px;
    scroll-margin-bottom: 140px;
    padding: 8px 12px;
    border: 1px solid #dadada;
    border-radius: 999px;
    background: #fff;
    color: #181818;
    font: inherit;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.3;
    text-align: left;
    box-sizing: border-box;
    transition: background-color 0.12s, border-color 0.12s;
    -webkit-tap-highlight-color: transparent;
  }

  .ma-tag:hover {
    border-color: #a4a4a4;
  }

  .ma-tag-selected,
  .ma-tag-selected:hover {
    border-color: #171717;
    background: #f1f1f1;
  }

  .ma-tag:disabled:not(.ma-tag-selected) {
    opacity: 0.5;
  }

  .ma-tag-emoji {
    font-family: "Apple Color Emoji", "Segoe UI Emoji",
      "Noto Color Emoji", sans-serif;
    font-size: 16px;
    line-height: 1.1;
    flex-shrink: 0;
  }

  /* Fixed tag navigation */

  .ma-tag-actions {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 102;
    background: #fff;
    border-top: 1px solid #ededed;
    padding: 10px 0 calc(14px + env(safe-area-inset-bottom, 0px));
    box-sizing: border-box;
  }

  .ma-tag-buttons {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 90%;
    max-width: 460px;
    margin: 0 auto;
  }

  /* Selected tags in review */

  .ma-review-tags {
    display: flex;
    gap: 7px;
    flex-wrap: wrap;
  }

  .ma-review-tags > span {
    display: inline-flex;
    align-items: center;
    padding: 5px 9px;
    border-radius: 999px;
    border: 1px solid #ddd;
    background: #f7f7f7;
    font-size: 12px;
    line-height: 1.5;
  }

  /* Reduced motion */

  @media (prefers-reduced-motion: reduce) {
    .ma-page * {
      scroll-behavior: auto !important;
    }
  }
`;
